/**
 * Alice — „Battle of Legends, Volume One".
 *
 * CURIOUSER AND CURIOUSER: pri rozostavení sa hráč rozhodne, či Alice začína
 * VEĽKÁ (BIG), alebo MALÁ (SMALL). Veľká má +2 k hodnote svojich útočných
 * kariet, malá +1 k obranným. Veľa kariet ju „zmení" (Change size) — z veľkej
 * na malú a naopak. Veľkosť je verejná (`players[i].form`), na stole je to
 * žetón pri figúrke. (BoL Vol. 1, „Setup" krok 5: rozhodnutia o figúrke pri
 * rozostavení, napr. veľkosť Alice.)
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, drawCard, emit, getMap, healFighter, millDeck } from '../ops.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { feint, momentousShift, regroup, skirmish } from './common.js';
import { askMove, fighterOrNull, opponentOf, revealHand } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const ALICE = 'alice';
const JABBERWOCK = 'the-jabberwock';
export type AliceSize = 'big' | 'small';

export function aliceSize(ctx: Ctx, player: PlayerIndex): AliceSize {
  return ctx.state.players[player]?.form === 'small' ? 'small' : 'big';
}

function setSize(ctx: Ctx, player: PlayerIndex, size: AliceSize): void {
  const seat = ctx.state.players[player];
  if (!seat) return;
  seat.form = size;
  emit(ctx, { type: 'specialUsed', player, heroId: ALICE, name: size === 'big' ? 'Alice je VEĽKÁ' : 'Alice je MALÁ' });
}

/** „Change size." */
function changeSize(ctx: Ctx, player: PlayerIndex): void {
  setSize(ctx, player, aliceSize(ctx, player) === 'big' ? 'small' : 'big');
}

const aliceOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === ALICE) ?? null;

/** „Move Alice up to N spaces. Change size." — veľkosť sa mení až po presune (voľba `auto`). */
function moveAliceThenChange(e: EffectCtx, max: number, label: string): void {
  const alice = aliceOf(e.ctx, e.player);
  if (alice) askMove(e, { fighters: [alice.id], max, label });
  askChangeAfter(e);
}

function askChangeAfter(e: EffectCtx): void {
  askChoice(e.ctx, {
    kind: 'auto',
    player: e.player,
    source: e.cardId,
    label: 'Zmena veľkosti',
    optional: false,
    follow: { kind: 'special', hero: ALICE, data: 'change' },
  });
}

/** „If the opposing fighter is a hero, this card's value is 5 instead." */
const clawsThatCatch: CardEffect = {
  during: (e, value) => (fighterOrNull(e.ctx, e.opponentFighterId)?.kind === 'hero' ? 5 : value),
};

/** „Move each of your fighters up to 2 spaces. Change size." */
const madAsAHatter: CardEffect = {
  after: (e) => {
    const ids: FighterId[] = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id);
    for (const id of ids) {
      askMove(e, { fighters: [id], max: 2, label: `Mad as a Hatter: presuň ${fighterOrNull(e.ctx, id)?.name ?? 'figúrku'} až o 2` });
    }
    askChangeAfter(e);
  },
};

/**
 * „Discard the top card of your deck. Add its BOOST value to this card's value."
 * Odhodenie je zmena stavu, preto ide v `duringChoice` (pred výpočtom hodnôt);
 * boost sa pripočíta k vlastnej strane súboja.
 */
const manxomeFoe: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const top = e.ctx.state.players[e.player]?.deck[0];
    if (!combat || !top) return;
    millDeck(e.ctx, e.player, 1);
    const side = e.role === 'defender' ? 'defender' : 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += cardDef(e.ctx, top).boost;
  },
};

/**
 * „Choose 2 different effects: draw 2 cards; Alice recovers 3 health; place
 * Alice in any other space." Dve voľby za sebou — druhá už bez prvej.
 */
const LOOKING_GLASS = 'alice.looking-glass';
const GLASS_OPTIONS: Record<string, string> = {
  draw: 'Potiahnuť 2 karty',
  heal: 'Alice si vylieči 3 zdravia',
  place: 'Položiť Alice na ľubovoľný iný priestor',
};

function askGlass(ctx: Ctx, player: PlayerIndex, source: string | null, taken: string[]): void {
  const left = Object.keys(GLASS_OPTIONS).filter((key) => !taken.includes(key));
  askChoice(ctx, {
    kind: 'option',
    player,
    source,
    label: `Looking Glass: vyber efekt (${taken.length + 1} z 2)`,
    optional: false,
    options: left.map((key) => ({
      label: GLASS_OPTIONS[key]!,
      follow: { kind: 'effect' as const, effect: LOOKING_GLASS, data: [...taken, key].join(',') },
    })),
  });
}

const lookingGlass: CardEffect = {
  after: (e) => askGlass(e.ctx, e.player, e.cardId, []),
  /**
   * `data` = doteraz vybrané efekty („draw,heal"); s predponou `next:` je to
   * pokyn ponúknuť druhý efekt — až keď sa dohrá prvý (napr. položenie Alice).
   */
  onFollow: (ctx, choice, data) => {
    if (data.startsWith('next:')) {
      askGlass(ctx, choice.player, choice.source, data.slice(5).split(','));
      return;
    }
    const taken = data.split(',');
    const picked = taken[taken.length - 1];
    const alice = aliceOf(ctx, choice.player);
    if (picked === 'draw') {
      drawCard(ctx, choice.player);
      drawCard(ctx, choice.player);
    } else if (picked === 'heal' && alice) {
      healFighter(ctx, alice.id, 3);
    } else if (picked === 'place' && alice) {
      askChoice(ctx, {
        kind: 'place',
        player: choice.player,
        source: choice.source,
        label: 'Looking Glass: polož Alice na ľubovoľný iný priestor',
        optional: false,
        fighterId: alice.id,
        spaces: getMap(ctx).spaces.map((s) => s.id).filter((id) => !ctx.state.fighters.some((f) => f.spaceId === id)),
      });
    }
    if (taken.length < 2) {
      askChoice(ctx, {
        kind: 'auto',
        player: choice.player,
        source: choice.source,
        label: 'Looking Glass: druhý efekt',
        optional: false,
        follow: { kind: 'effect', effect: LOOKING_GLASS, data: `next:${data}` },
      });
    }
  },
};

/** „If you won the combat, look at your opponent's hand and choose 1 card for them to discard." */
const snickerSnack: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    const opponent = opponentOf(e);
    if (opponent === null || (e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) return;
    if (!revealHand(e, opponent)) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Snicker-Snack: vyber kartu, ktorú súper odhodí',
      optional: false,
      from: 'hand',
      owner: opponent,
      purpose: 'discard',
    });
  },
};

/** „Change size." */
const oFrabjousDay: CardEffect = {
  after: (e) => changeSize(e.ctx, e.player),
};

/** „Move Alice up to 3 spaces. Change size." */
const otherSideOfTheMushroom: CardEffect = {
  after: (e) => moveAliceThenChange(e, 3, 'The Other Side of the Mushroom: presuň Alice až o 3'),
};

/** „Move Alice up to 3 spaces. Change size." */
const eatMe: CardEffect = {
  scheme: (e) => moveAliceThenChange(e, 3, 'Eat Me: presuň Alice až o 3'),
};

/** „Move Alice up to 5 spaces. Change size." */
const imLate: CardEffect = {
  after: (e) => moveAliceThenChange(e, 5, "I'm Late! I'm Late!: presuň Alice až o 5"),
};

/** „Draw 2 cards. Change size." */
const drinkMe: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    changeSize(e.ctx, e.player);
  },
};

/** „Deal 2 damage to any one fighter adjacent to the Jabberwock." */
const jawsThatBite: CardEffect = {
  after: (e) => {
    const jabberwock = e.ctx.state.fighters.find((f) => f.owner === e.player && f.defId === JABBERWOCK);
    if (!jabberwock) return;
    const around = new Set(neighbors(getMap(e.ctx), jabberwock.spaceId));
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Jaws That Bite: 2 poškodenia figúrke pri Jabberwockovi',
      optional: false,
      amount: 2,
      targets: e.ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id),
    });
  },
};

export const aliceCards: EffectRegistry = {
  'alice.claws-that-catch': clawsThatCatch,
  'alice.momentous-shift': momentousShift,
  'alice.skirmish': skirmish(2),
  'alice.mad-as-a-hatter': madAsAHatter,
  'alice.manxome-foe': manxomeFoe,
  'alice.feint': feint,
  'alice.regroup': regroup,
  [LOOKING_GLASS]: lookingGlass,
  'alice.snicker-snack': snickerSnack,
  'alice.o-frabjous-day': oFrabjousDay,
  'alice.the-other-side-of-the-mushroom': otherSideOfTheMushroom,
  'alice.eat-me': eatMe,
  'alice.i-m-late-i-m-late': imLate,
  'alice.drink-me': drinkMe,
  'alice.jaws-that-bite': jawsThatBite,
};

/** Útočí alebo sa bráni práve Alice (nie Jabberwock)? */
const isAlice = (ctx: Ctx, fighterId: FighterId | null) => fighterOrNull(ctx, fighterId)?.defId === ALICE;

export const aliceSpecial: HeroSpecial = {
  name: 'Curiouser and Curiouser',
  setup: (ctx, player) => {
    const follow = (data: string) => ({ kind: 'special' as const, hero: ALICE, data });
    askChoice(ctx, {
      kind: 'option',
      player,
      source: null,
      label: 'Alice začína hru VEĽKÁ, alebo MALÁ?',
      optional: false,
      options: [
        { label: 'VEĽKÁ — +2 k jej útočným kartám', follow: follow('big') },
        { label: 'MALÁ — +1 k jej obranným kartám', follow: follow('small') },
      ],
    });
  },
  onFollow: (ctx, player, data) => {
    if (data === 'change') changeSize(ctx, player);
    else setSize(ctx, player, data === 'small' ? 'small' : 'big');
  },
  attackModifier: (ctx, player, fighterId) => (isAlice(ctx, fighterId) && aliceSize(ctx, player) === 'big' ? 2 : 0),
  defenseModifier: (ctx, player, fighterId) => (isAlice(ctx, fighterId) && aliceSize(ctx, player) === 'small' ? 1 : 0),
};
